import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button } from '@parrot-software/design-system';
import { Pencil, Split } from '@parrot-software/design-system/icons';
import { toast } from 'sonner';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import type { GroupingAction } from '@subscriptions/presentation/constants';
import { AnalyticsStatuses, GroupingActions } from '@subscriptions/presentation/constants';
import { CustomerStates } from '@subscriptions/domain/types/SubscriptionsConst';
import {
  DeleteSubscriptionBlockDialog,
  EditSubscriptionBlockSheet,
} from '@subscriptions/presentation/components/specific/EditSubscriptionBlock';
import { useDeleteSubscriptionBlock, useSubscriptionAnalytics } from '@subscriptions/presentation/hooks';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';
import { resolveApiErrorCode, resolveSubscriptionErrorMessage } from '@subscriptions/presentation/utils';

interface BlockDetailActionsProps {
  areActionsBlocked?: boolean;
  block: SubscriptionBlockViewDto;
}

/**
 * Edit and ungroup actions of a subscription block, with the sheet and dialog each one opens.
 *
 * Sits in the page header, so it stays reachable from every tab. Ungrouping only makes sense for
 * a block that holds more than one store.
 *
 * @param {Object} props - Component properties
 * @param {boolean} [props.areActionsBlocked] - When true, both actions are disabled because another block is configuring
 * @param {SubscriptionBlockViewDto} props.block - Subscription block data
 * @returns {React.ReactElement} Action buttons
 */
export const BlockDetailActions = ({ areActionsBlocked = false, block }: BlockDetailActionsProps) => {
  const { t, i18n } = useTranslation('subscriptions');
  const history = useHistory();
  const { mutateAsync: deleteSubscriptionBlock, isLoading: isDeleting } = useDeleteSubscriptionBlock();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { trackConfigurationFinished, trackConfigurationStarted } = useSubscriptionAnalytics();

  // Both surfaces are opened from here, so the open transition is tracked here and not inside
  // them: an effect would fire again whenever the block refetches while the surface stays open.
  const openWith = (action: GroupingAction, open: (value: boolean) => void) => {
    trackConfigurationStarted({
      action,
      blockUuid: block.uuid,
      storesCount: block.stores.length,
    });
    open(true);
  };

  const isInactive = block.customerState === CustomerStates.INACTIVE;
  const areActionsLocked = useAreSubscriptionActionsLocked();
  const areActionsDisabled = areActionsBlocked || isInactive || areActionsLocked;
  const canDelete = block.stores.length >= 2;

  const handleConfirmDelete = async () => {
    const common = {
      action: GroupingActions.SEPARATE,
      blockUuid: block.uuid,
      groupInvoices: block.groupInvoice,
      storesAdded: 0,
      storesRemoved: block.stores.length,
    };

    try {
      await deleteSubscriptionBlock(block.uuid);
      trackConfigurationFinished({ ...common, status: AnalyticsStatuses.SUCCESS });
      history.push('/subscriptions');
    } catch (error) {
      trackConfigurationFinished({
        ...common,
        errorCode: resolveApiErrorCode(error),
        status: AnalyticsStatuses.FAILED,
      });
      setIsDeleteOpen(false);
      toast.error(resolveSubscriptionErrorMessage(error, t, i18n, 'blockDelete.error'));
    }
  };

  return (
    <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:gap-3">
      <Button
        className="min-h-11 w-full sm:min-h-0 sm:w-auto"
        disabled={areActionsDisabled}
        onClick={() => openWith(GroupingActions.EDIT, setIsEditOpen)}
        variant="secondary"
      >
        <Pencil className="mr-2 h-4 w-4" />
        {t('blockDetail.actions.edit')}
      </Button>

      {canDelete && (
        <Button
          className="min-h-11 w-full sm:min-h-0 sm:w-auto"
          disabled={areActionsDisabled}
          onClick={() => openWith(GroupingActions.SEPARATE, setIsDeleteOpen)}
          variant="secondary"
        >
          <Split className="mr-2 h-4 w-4" />
          {t('blockDetail.actions.delete')}
        </Button>
      )}

      <EditSubscriptionBlockSheet
        block={block}
        onClose={() => setIsEditOpen(false)}
        onOpenChange={setIsEditOpen}
        open={isEditOpen}
      />

      <DeleteSubscriptionBlockDialog
        blockName={block.name}
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onOpenChange={setIsDeleteOpen}
        open={isDeleteOpen}
      />
    </div>
  );
};
